import Decimal from 'decimal.js';
import { formatAmount } from 'utils/amountUtils';
import { formatDate } from 'utils/date';
import { statusToText } from 'utils/status-to-text';
import { formatUnits } from 'viem';
import { useFeeInfo } from 'views/hooks/useTransactionsDetails';
import { formatNumberForResponse } from 'views/types/number';
import { IDepositExtended } from 'views/types/typeTransaction';

const SwapDetails: React.FC<{ transaction: IDepositExtended }> = ({
  transaction,
}) => {
  const feeInfo = useFeeInfo(transaction);

  const calculateExecutedRate = () => {
    const amountIn = +formatUnits(
      BigInt(transaction?.amountIn || '0'),
      transaction.currencyIn.decimals
    );

    const amountOut = +formatUnits(
      BigInt(transaction?.amountOut || '0'),
      transaction.currencyOut.decimals
    );

    if (amountIn > 0 && amountOut > 0) {
      const fallbackRate = +amountOut / +amountIn;
      const rate =
        transaction.swapType === 'SELL'
          ? +transaction.swapRate
          : 1 / +transaction.swapRate;
      return formatNumberForResponse(rate || fallbackRate, 12);
    }
    return '---';
  };

  const totalFee = feeInfo.reduce(
    (acc, fee) => acc + parseFloat(fee.amount || '0'),
    0
  );

  return (
    <ul className="flex flex-col gap-3">
      <li>
        Type: <span className="font-semibold ml-3 capitalize">Swap</span>
      </li>

      <li>
        Client ID:{' '}
        <span className="font-semibold ml-3">
          {transaction?.user?.coins_fly_id || '---'}
        </span>
      </li>

      <li>
        Currency in:{' '}
        <span className="font-semibold ml-3">
          {transaction?.currencyIn?.symbol || '---'}
        </span>
      </li>

      <li>
        Currency out:{' '}
        <span className="font-semibold ml-3">
          {transaction?.currencyOut?.symbol || '---'}
        </span>
      </li>

      <li>
        Amount Debited:{' '}
        <span className="font-semibold ml-3">
          {formatAmount(
            formatUnits(
              BigInt(transaction?.amountIn),
              transaction?.currencyIn?.decimals || 0
            )
          )}{' '}
          {transaction?.currencyIn?.symbol || '---'}
        </span>
      </li>

      <li>
        Amount Credited:{' '}
        <span className="font-semibold ml-3">
          {formatAmount(
            new Decimal(
              formatUnits(
                BigInt(transaction?.amountOut),
                transaction?.currencyOut?.decimals || 0
              )
            )
              .add(totalFee)
              .toNumber()
          )}{' '}
          {transaction?.currencyOut?.symbol || '---'}
        </span>
      </li>

      <li>
        Executed Rate:{' '}
        <span className="font-semibold ml-3">
          1 {transaction?.currencyIn?.symbol} = {calculateExecutedRate()}{' '}
          {transaction?.currencyOut?.symbol}
        </span>
      </li>

      {feeInfo.length > 0 && (
        <li className="flex flex-col">
          <div>
            Amount:{' '}
            <span className="font-semibold ml-3">
              {formatAmount(
                +formatUnits(
                  BigInt(transaction?.amountOut),
                  transaction?.currencyOut?.decimals || 0
                )
              )}{' '}
              {transaction?.currencyOut?.symbol}
            </span>
          </div>
          <div className="ml-3 mt-2">
            <div className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">
              Fee Breakdown (Client Fees):
            </div>
            {feeInfo.map((fee, index) => (
              <div key={index} className="text-sm ml-2">
                {fee.type}:{' '}
                <span className="font-medium">
                  {fee.amount} {fee.currency}
                </span>
              </div>
            ))}
          </div>
        </li>
      )}

      <li>
        Date:{' '}
        <span className="font-semibold ml-3">
          {transaction?.createdAt
            ? formatDate(transaction.createdAt, 'DD.MM.YYYY HH:mm')
            : '---'}
        </span>
      </li>

      {transaction?.completedOrFailedAt && (
        <li>
          Status Changed at:{' '}
          <span className="font-semibold ml-3">
            {formatDate(transaction.completedOrFailedAt, 'DD.MM.YYYY HH:mm')}
          </span>
        </li>
      )}

      <li>
        Transaction status:{' '}
        <span className="font-semibold ml-3 capitalize">
          {statusToText(transaction?.status, transaction?.type)}
        </span>
      </li>

      <li>
        Order: <span className="font-semibold ml-3">{transaction?.id}</span>
      </li>
    </ul>
  );
};
export default SwapDetails;
